import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { AbstractTile } from "@/components/design/AbstractTile";
import { Section } from "@/components/design/Section";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  fetchTitleDetail,
  TobisNotConfiguredError,
  type TobisCastMember,
  type TobisTitleDetail,
  type TobisTitleScript,
} from "@/lib/tobis";
import { logApplicationFailure } from "@/lib/user-error";
import { useTobisFavorites } from "@/lib/use-tobis-favorites";
import { TobisAdmissionsChart } from "./TobisAdmissionsChart";
import { TobisAdmissionsModal } from "./TobisAdmissionsModal";
import { TobisDossier } from "./TobisDossier";
import { TobisFavoriteButton } from "./TobisFavoriteButton";
import { BarStat } from "./TobisMarketCharts";
import { TobisPageShell } from "./TobisPageShell";
import { TobisPersonRow } from "./TobisPersonRow";

// Label oben, Control darunter — dieselbe Huelle wie das SHARE-FILTER-Feld in
// ExtendedSearchPage.tsx und der Register-Filter in TobisFilters.tsx (dort
// jeweils lokal "Field" genannt).
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.15em] text-ink/45">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

type CastRoleFilter = "" | "acting" | "directing" | "producing" | "crew";

// Dieselbe Kategorisierung wie ROLE_TYPE_CONDITION im BFF (apps/bff/src/tobis/filters.ts),
// hier auf einen einzelnen Titel-Cast statt auf das ganze Talent-Register
// angewandt. "Crew" bleibt bewusst der breiteste Eimer (jeder nicht-
// schauspielerische Job auf diesem Titel), Regie/Produktion greifen zusaetzlich
// enger. Eine Person kann mehrere Rollentypen gleichzeitig erfuellen.
const PRODUCING_JOBS = ["Producer", "Executive Producer", "Co-Producer", "Line Producer"];

function matchesCastRoleFilter(member: TobisCastMember, filter: CastRoleFilter): boolean {
  switch (filter) {
    case "":
      return true;
    case "acting":
      return member.characterName !== null;
    case "directing":
      return member.jobs.includes("Director");
    case "producing":
      return member.jobs.some((job) => PRODUCING_JOBS.includes(job));
    case "crew":
      return member.jobs.length > 0;
  }
}

// Titel-Detail im Zuschnitt der SHARE-Detailansicht: Cover und Stammdaten links,
// Inhalt in der Mitte, Cast in der rechten Spalte (dort, wo bei SharedItem die
// Kommentare stehen).

// href optional: verlinkt das Label auf die Quelle der Zahl, damit die Angabe
// pruefbar bleibt (IMDb, TMDB oder LUMIERE, je nachdem woher der Wert stammt).
// onClick optional: klappt die Zahl auf der Seite selbst auf (Kinobesucher je
// Markt). Pfeil-Konvention aus UI_CONVENTIONS.md: ↗ fuer "verlaesst die Seite",
// › fuer Aufklappen - beide klein, getrackt, als Deko in aria-hidden.
function Fact({
  label,
  value,
  href,
  onClick,
}: {
  label: string;
  value: string | null;
  href?: string;
  onClick?: () => void;
}) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[12px] text-ink/45">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 transition hover:text-accent"
          >
            {label} <span aria-hidden="true">↗</span>
          </a>
        ) : (
          label
        )}
      </dt>
      <dd className="mt-0.5 text-[13px] text-ink/80">
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            className="inline-flex items-start gap-1 rounded text-left transition hover:text-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink/30"
          >
            {value} <span aria-hidden="true">›</span>
          </button>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/** IMDb-Titelseite: Quelle fuer IMDb-Rating/Stimmen und - da OMDb IMDb-Daten
 *  spiegelt - auch fuer das OMDb-Einspielergebnis. */
function imdbUrl(imdbId: string | null): string | undefined {
  return imdbId ? `https://www.imdb.com/title/${imdbId}/` : undefined;
}

/** TMDB-Titelseite: Quelle fuer Originaltitel, Budget und Welt-Einspielergebnis. */
function tmdbUrl(tmdbId: string | null): string | undefined {
  return tmdbId ? `https://www.themoviedb.org/movie/${tmdbId}` : undefined;
}

/**
 * Quelle der DE-Besucherzahl. Nur fuer LUMIERE: die FFA-Hitlisten sind PDFs
 * ohne browsierbare Einzelseite je Titel, dafuer gibt es keinen ehrlichen Link.
 */
function admissionsSourceUrl(source: string | null, lumiereId: string | null): string | undefined {
  return source === "lumiere" && lumiereId
    ? `https://lumiere.obs.coe.int/movie/${lumiereId}/`
    : undefined;
}

/**
 * Verweis auf die Fassung bei ihrer Quelle, nicht auf eine eigene Kopie -
 * dieselbe Linie wie beim Text selbst (V0030/V0037): nur die Kennzahlen sind
 * Produktinhalt, der Link zeigt auf die Quelle, die geprueft werden kann.
 */
function scriptSourceUrl(script: TobisTitleScript | null): string | undefined {
  if (!script) return undefined;
  if (script.source === "scriptslug") return script.pdfUrl ?? undefined;
  if (script.source === "imsdb" && script.scriptPath) return `https://imsdb.com${script.scriptPath}`;
  return undefined;
}

function scriptSourceLabel(source: string): string {
  return source === "scriptslug" ? "Script Slug" : "IMSDb";
}

/** Wikidata-Entity-Seite, Kennung wie "Q44578". */
function wikidataUrl(wikidataId: string | null): string | undefined {
  return wikidataId ? `https://www.wikidata.org/wiki/${wikidataId}` : undefined;
}

/**
 * EIDR-IDs sind als DOI registriert (Praefix 10.5240), doi.org loest sie
 * technisch korrekt auf, landet aber auf dem rohen XML des Resolvers
 * (resolve.eidr.org, Content-Type text/xml) statt einer lesbaren Seite.
 * ui.eidr.org ist die eigentliche, fuer Menschen gedachte Lookup-Seite der
 * Registry. ISAN hat keine vergleichbare browsierbare Einzel-URL, dafuer
 * gibt es keinen ehrlichen Link - bleibt unverlinkt.
 */
function eidrUrl(eidr: string | null): string | undefined {
  return eidr ? `https://ui.eidr.org/content/${eidr}` : undefined;
}

export function TobisTitlePage() {
  const intl = useIntl();
  const { tvmazeId } = useParams<{ tvmazeId: string }>();
  const [data, setData] = useState<TobisTitleDetail | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [admissionsOpen, setAdmissionsOpen] = useState(false);
  const [castRoleFilter, setCastRoleFilter] = useState<CastRoleFilter>("");
  const favorites = useTobisFavorites();

  const load = useCallback(async (id: string) => {
    try {
      setData(await fetchTitleDetail(id));
      setPhase("ready");
    } catch (err) {
      if (!(err instanceof TobisNotConfiguredError)) logApplicationFailure("tobis.titleDetail");
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    if (tvmazeId) void load(tvmazeId);
  }, [tvmazeId, load]);

  const t = data?.title;
  const admissions = data?.admissions ?? null;
  const script = data?.script ?? null;
  const identifiers = data?.identifiers ?? null;
  const filteredCast = useMemo(
    () => data?.cast.filter((m) => matchesCastRoleFilter(m, castRoleFilter)) ?? [],
    [data?.cast, castRoleFilter],
  );
  const from = t?.premiered?.slice(0, 4) ?? (t?.year === null ? "" : String(t?.year ?? ""));
  const to = t?.ended?.slice(0, 4) ?? "";
  const years =
    from && to
      ? intl.formatMessage({ id: "tobis.person.yearRange", defaultMessage: "{from} to {to}" }, { from, to })
      : from
        ? intl.formatMessage({ id: "tobis.person.sinceYear", defaultMessage: "{year} until today" }, { year: from })
        : "";

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={t?.name ?? ""}
      // t.id und nicht der URL-Parameter: gemerkt wird die Id, unter der der
      // Titel im Bestand steht (siehe favorites.ts im BFF).
      action={
        t ? (
          <TobisFavoriteButton
            marked={favorites.isFavorite("title", t.id)}
            onToggle={() => favorites.toggle("title", t.id)}
          />
        ) : undefined
      }
    >
      <div>
        <Link to="/tobis/titles" className="text-[13px] text-ink/55 transition hover:text-ink">
          {intl.formatMessage({ id: "tobis.title.back", defaultMessage: "Back to titles" })}
        </Link>
      </div>

      {phase === "error" ? (
        <Section title={intl.formatMessage({ id: "tobis.title.about", defaultMessage: "Title" })}>
          <p className="text-sm text-ink/55">
            {intl.formatMessage({
              id: "tobis.loadError",
              defaultMessage: "The overview could not be loaded.",
            })}
          </p>
        </Section>
      ) : null}

      {t ? (
        <div className="grid grid-cols-1 gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)] @lg:items-start">
          <div className="box bg-surface p-5">
            <div className="overflow-hidden rounded-md">
              {t.coverUrl ? (
                <img src={t.coverUrl} alt="" className="aspect-[2/3] w-full object-cover" />
              ) : (
                <AbstractTile variant="dots" hue={30} className="aspect-[2/3] w-full" />
              )}
            </div>
            <dl className="mt-4 space-y-3">
              <Fact
                label={intl.formatMessage({ id: "tobis.title.years", defaultMessage: "Aired" })}
                value={years || null}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.kind", defaultMessage: "Kind" })}
                value={t.kind}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.status", defaultMessage: "Status" })}
                value={t.status}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.runtime", defaultMessage: "Runtime" })}
                value={
                  t.runtimeMin === null
                    ? null
                    : intl.formatMessage(
                        { id: "tobis.title.minutes", defaultMessage: "{count} min" },
                        { count: t.runtimeMin },
                      )
                }
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.network", defaultMessage: "Network" })}
                value={t.network}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.country", defaultMessage: "Country" })}
                value={t.country}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.imdb", defaultMessage: "IMDb id" })}
                value={t.imdbId}
                href={imdbUrl(t.imdbId)}
              />
              <Fact
                label={intl.formatMessage({
                  id: "tobis.title.originalTitle",
                  defaultMessage: "Original title",
                })}
                value={t.originalTitle}
                href={tmdbUrl(t.tmdbId)}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.wikidata", defaultMessage: "Wikidata" })}
                value={identifiers?.wikidataId ?? null}
                href={wikidataUrl(identifiers?.wikidataId ?? null)}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.eidr", defaultMessage: "EIDR" })}
                value={identifiers?.eidr ?? null}
                href={eidrUrl(identifiers?.eidr ?? null)}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.title.isan", defaultMessage: "ISAN" })}
                value={identifiers?.isan ?? null}
              />
            </dl>
          </div>

          <div className="space-y-6">
            <div className="box bg-surface p-5 @sm:p-6">
              <div className="text-[10px] tracking-[0.25em] text-ink/55">
                {t.genres.join(" · ")}
              </div>
              <h2 className="mt-3 font-serif text-3xl leading-tight @sm:text-4xl">{t.name}</h2>
              {t.summary ? (
                <p className="mt-4 text-sm leading-relaxed text-ink/70">{t.summary}</p>
              ) : null}
              {t.keywords.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {t.keywords.map((k) => (
                    <span
                      key={k}
                      className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] text-ink/70"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <Section
              title={intl.formatMessage({ id: "tobis.title.signals", defaultMessage: "Signals" })}
            >
              <dl className="grid grid-cols-2 gap-4 @sm:grid-cols-4">
                <Fact
                  label={intl.formatMessage({ id: "tobis.title.rating", defaultMessage: "IMDb" })}
                  value={t.imdbRating}
                  href={imdbUrl(t.imdbId)}
                />
                <Fact
                  label={intl.formatMessage({ id: "tobis.title.votes", defaultMessage: "Votes" })}
                  value={t.imdbVotes ? intl.formatNumber(Number(t.imdbVotes)) : null}
                />
                <Fact
                  label={intl.formatMessage({ id: "tobis.title.tmdbScore", defaultMessage: "TMDB" })}
                  value={t.tmdbScore}
                  href={tmdbUrl(t.tmdbId)}
                />
                <Fact
                  label={intl.formatMessage({
                    id: "tobis.title.metascore",
                    defaultMessage: "Metascore",
                  })}
                  value={t.metascore}
                />
                <Fact
                  label={intl.formatMessage({ id: "tobis.title.wins", defaultMessage: "Awards" })}
                  value={t.awardWins === null ? null : String(t.awardWins)}
                />
                <Fact
                  label={intl.formatMessage({
                    id: "tobis.title.boxOffice",
                    defaultMessage: "Box office (OMDb)",
                  })}
                  value={
                    t.boxOfficeUsd
                      ? intl.formatNumber(Number(t.boxOfficeUsd), {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })
                      : null
                  }
                />
                <Fact
                  label={intl.formatMessage({ id: "tobis.title.budget", defaultMessage: "Budget" })}
                  value={
                    t.budgetUsd
                      ? intl.formatNumber(Number(t.budgetUsd), {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })
                      : null
                  }
                  href={tmdbUrl(t.tmdbId)}
                />
                <Fact
                  label={intl.formatMessage({
                    id: "tobis.title.revenue",
                    defaultMessage: "Revenue (worldwide, TMDB)",
                  })}
                  value={
                    t.revenueUsd
                      ? intl.formatNumber(Number(t.revenueUsd), {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })
                      : null
                  }
                  href={tmdbUrl(t.tmdbId)}
                />
                <Fact
                  label={intl.formatMessage({
                    id: "tobis.title.popularity",
                    defaultMessage: "Popularity",
                  })}
                  value={t.popularity === null ? null : String(t.popularity)}
                />
                {/* Drehbuchfassung (IMSDb/Script Slug): eine Kennzahl zum
                    Produktionsaufwand, die keine andere Quelle hier liefert.
                    Der Link zeigt auf die Fassung bei der Quelle, nicht auf
                    eine eigene Kopie (V0030/V0037). */}
                <Fact
                  label={intl.formatMessage({ id: "tobis.title.script", defaultMessage: "Screenplay" })}
                  value={
                    script
                      ? intl.formatMessage(
                          {
                            id: "tobis.title.scriptValue",
                            defaultMessage: "{scenes} scenes, {characters} roles ({source})",
                          },
                          {
                            scenes: script.scenes ?? "?",
                            characters: script.characters ?? "?",
                            source: scriptSourceLabel(script.source),
                          },
                        )
                      : null
                  }
                  href={scriptSourceUrl(script)}
                />
                {/* Kinobesucher: die Summe ueber alle Maerkte, die LUMIERE
                    fuehrt, und ein Klick auf die Verteilung dahinter. Nur wenn
                    die Quelle nichts hat (Titel mit reiner FFA-Zahl), bleibt
                    die DE-Zahl allein stehen - sonst verschwaende der Slot
                    die breitere Aussage. */}
                {admissions ? (
                  <Fact
                    label={intl.formatMessage({
                      id: "tobis.title.admissionsTotal",
                      defaultMessage: "Admissions, total",
                    })}
                    value={intl.formatMessage(
                      {
                        id: "tobis.title.admissionsTotalValue",
                        defaultMessage: "{count} (LUMIERE, {markets} markets)",
                      },
                      {
                        count: intl.formatNumber(Number(admissions.total)),
                        markets: admissions.marketCount,
                      },
                    )}
                    onClick={() => setAdmissionsOpen(true)}
                  />
                ) : (
                  <Fact
                    label={intl.formatMessage({
                      id: "tobis.title.admissionsDe",
                      defaultMessage: "DE admissions",
                    })}
                    value={
                      t.admissionsDe
                        ? intl.formatMessage(
                            {
                              id: "tobis.title.admissionsDeValue",
                              defaultMessage: "{count} ({source})",
                            },
                            {
                              count: intl.formatNumber(Number(t.admissionsDe)),
                              // FFA ist eine veroeffentlichte Abrechnung (gemessen),
                              // LUMIERE nennt seine Zahlen selbst Mindestwerte
                              // (geschaetzt) - siehe model.admissions_de.
                              source:
                                t.admissionsSource === "ffa"
                                  ? intl.formatMessage({
                                      id: "tobis.title.admissionsMeasured",
                                      defaultMessage: "measured, FFA",
                                    })
                                  : intl.formatMessage({
                                      id: "tobis.title.admissionsEstimated",
                                      defaultMessage: "estimated, LUMIERE",
                                    }),
                            },
                          )
                        : null
                    }
                    href={admissionsSourceUrl(t.admissionsSource, t.lumiereId)}
                  />
                )}
              </dl>
              {t.awardsText ? (
                <p className="mt-4 text-[12px] text-ink/40">{t.awardsText}</p>
              ) : null}
            </Section>

            {(data?.admissionsSeries.length ?? 0) > 1 ? (
              <Section
                title={intl.formatMessage({
                  id: "tobis.title.admissionsSeries",
                  defaultMessage: "Admissions over time (Germany)",
                })}
              >
                <TobisAdmissionsChart points={data?.admissionsSeries ?? []} />
              </Section>
            ) : null}

            {/* Erst ab zwei Staenden: ein einzelner Balken ist kein Verlauf,
                und der aktuelle Wert steht schon als Fact daneben. Balken
                statt Linie, weil die Abrufdaten unregelmaessig liegen - eine
                Linie wuerde dazwischen Kontinuitaet vortaeuschen. */}
            {(data?.revenueHistory.length ?? 0) > 1 ? (
              <Section
                title={intl.formatMessage({
                  id: "tobis.title.revenueHistory",
                  defaultMessage: "Revenue over time (TMDB)",
                })}
              >
                <BarStat
                  data={data?.revenueHistory ?? []}
                  xKey="fetchedOn"
                  yKey="revenueUsd"
                  valueLabel={intl.formatMessage({
                    id: "tobis.title.revenue",
                    defaultMessage: "Revenue (worldwide, TMDB)",
                  })}
                  formatX={(v) =>
                    intl.formatDate(new Date(String(v)), { day: "numeric", month: "short", year: "numeric" })
                  }
                  formatY={(v) =>
                    intl.formatNumber(v, {
                      style: "currency",
                      currency: "USD",
                      notation: "compact",
                      maximumFractionDigits: 1,
                    })
                  }
                />
                <p className="mt-2 text-[11px] text-ink/40">
                  {intl.formatMessage({
                    id: "tobis.title.revenueHistoryNote",
                    defaultMessage:
                      "Worldwide gross as recorded by TMDB at each of our fetch dates. Intervals are irregular, and the figure is only as current as TMDB's editors keep it: a size class, not an official accounting figure.",
                  })}{" "}
                  <a
                    href={tmdbUrl(t.tmdbId)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink/55 underline underline-offset-2 hover:text-ink"
                  >
                    {intl.formatMessage({ id: "tobis.title.revenueHistorySource", defaultMessage: "Source: TMDB" })}
                  </a>
                </p>
              </Section>
            ) : null}

            {(data?.foerderung.length ?? 0) > 0 || (data?.hitlisteLegacy.length ?? 0) > 0 ? (
              <Section
                title={intl.formatMessage({ id: "tobis.title.funding", defaultMessage: "FFA funding & chart entries" })}
              >
                {data?.foerderung.map((f, i) => (
                  <div key={`${f.bereich}-${f.jahr}-${i}`} className="border-b border-ink/10 py-2 last:border-0">
                    <div className="text-[13px] text-ink/80">
                      {f.bereich}
                      {f.jahr ? <span className="text-ink/45"> · {f.jahr}</span> : null}
                    </div>
                    <div className="mt-0.5 text-[12px] text-ink/45">
                      {[
                        f.antragsteller,
                        f.foerdersummeEur != null
                          ? intl.formatMessage(
                              { id: "tobis.title.fundingAmount", defaultMessage: "{amount} €" },
                              { amount: intl.formatNumber(f.foerdersummeEur) },
                            )
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                ))}
                {(data?.foerderung.length ?? 0) > 0 ? (
                  <a
                    href="https://www.ffa.de/dfff-gefoerderte-filme.html"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-[11px] text-ink/55 underline underline-offset-2 hover:text-ink"
                  >
                    {intl.formatMessage({
                      id: "tobis.title.fundingSource",
                      defaultMessage: "FFA funding overview (current list, not the archived filing)",
                    })}
                  </a>
                ) : null}
                {data?.hitlisteLegacy.map((h, i) => (
                  <div key={`${h.listPeriod}-${h.listYear}-${i}`} className="border-b border-ink/10 py-2 last:border-0">
                    <div className="text-[13px] text-ink/80">
                      {intl.formatMessage(
                        { id: "tobis.title.hitlisteLegacyEntry", defaultMessage: "{year} ({period})" },
                        { year: h.listYear, period: h.listScope ?? h.listPeriod },
                      )}
                      {h.sourceUrl ? (
                        <a
                          href={h.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-2 text-[11px] text-ink/55 underline underline-offset-2 hover:text-ink"
                        >
                          PDF
                        </a>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-[12px] text-ink/45">
                      {h.admissionsPeriod != null
                        ? intl.formatMessage(
                            { id: "tobis.title.hitlisteLegacyAdmissions", defaultMessage: "{count} admissions" },
                            { count: intl.formatNumber(h.admissionsPeriod) },
                          )
                        : null}
                      {h.rank != null ? ` · #${h.rank}` : null}
                      {h.distributor ? ` · ${h.distributor}` : null}
                    </div>
                  </div>
                ))}
              </Section>
            ) : null}

            {t?.dfff ? (
              <Section
                title={intl.formatMessage({ id: "tobis.title.dfff", defaultMessage: "DFFF funding project" })}
              >
                <dl className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.regie", defaultMessage: "Director" })}
                    value={t.dfff.regie}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.autor", defaultMessage: "Writer" })}
                    value={t.dfff.autor}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.darsteller", defaultMessage: "Cast" })}
                    value={t.dfff.darsteller}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.produzent", defaultMessage: "Producer" })}
                    value={t.dfff.produzent}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.koproduzenten", defaultMessage: "Co-producers" })}
                    value={t.dfff.koproduzenten}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.verleih", defaultMessage: "Distributor" })}
                    value={t.dfff.verleih}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.produktionsdienstleister", defaultMessage: "Production service" })}
                    value={t.dfff.produktionsdienstleister}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.drehbeginn", defaultMessage: "Shoot start" })}
                    value={t.dfff.drehbeginn}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.foerderung", defaultMessage: "DFFF grant" })}
                    value={t.dfff.foerderungEur != null ? `${intl.formatNumber(t.dfff.foerderungEur)} €` : null}
                  />
                  <Fact
                    label={intl.formatMessage({ id: "tobis.title.dfff.zuschuss", defaultMessage: "DFFF grant (co-financing)" })}
                    value={t.dfff.zuschussEur != null ? `${intl.formatNumber(t.dfff.zuschussEur)} €` : null}
                  />
                </dl>
                {t.dfff.sourceUrl ? (
                  <a
                    href={t.dfff.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-[11px] text-ink/55 underline underline-offset-2 hover:text-ink"
                  >
                    {intl.formatMessage({ id: "tobis.title.dfff.source", defaultMessage: "DFFF project list (source)" })}
                  </a>
                ) : null}
              </Section>
            ) : null}

            <Section
              title={intl.formatMessage({ id: "tobis.title.cast", defaultMessage: "Cast" })}
              right={<span className="text-[12px] text-ink/40">{filteredCast.length}</span>}
              empty={(data?.cast.length ?? 0) === 0}
              emptyLabel={intl.formatMessage({
                id: "tobis.title.castEmpty",
                defaultMessage: "No cast recorded.",
              })}
            >
              {/* Dieselben Tab-Werte und -Labels wie der Register-Filter
                  (TobisFilters.tsx): ein langer Cast wird sonst zu einer
                  Scrollstrecke statt einer Uebersicht. */}
              <Field label={intl.formatMessage({ id: "tobis.filter.roleType", defaultMessage: "Type" })}>
                <SegmentedControl
                  value={castRoleFilter}
                  onChange={setCastRoleFilter}
                  size="sm"
                  options={[
                    {
                      value: "",
                      label: intl.formatMessage({ id: "tobis.filter.all", defaultMessage: "All" }),
                    },
                    {
                      value: "acting",
                      label: intl.formatMessage({
                        id: "tobis.filter.roleTypeActing",
                        defaultMessage: "Actors",
                      }),
                    },
                    {
                      value: "directing",
                      label: intl.formatMessage({
                        id: "tobis.filter.roleTypeDirecting",
                        defaultMessage: "Directing",
                      }),
                    },
                    {
                      value: "producing",
                      label: intl.formatMessage({
                        id: "tobis.filter.roleTypeProducing",
                        defaultMessage: "Producing",
                      }),
                    },
                    {
                      value: "crew",
                      label: intl.formatMessage({
                        id: "tobis.filter.roleTypeCrew",
                        defaultMessage: "Crew",
                      }),
                    },
                  ]}
                />
              </Field>
              {filteredCast.length === 0 ? (
                <p className="mt-4 text-sm text-ink/55">
                  {intl.formatMessage({
                    id: "tobis.title.castFilterEmpty",
                    defaultMessage: "No one found for this type.",
                  })}
                </p>
              ) : (
                <div className="mt-4 space-y-2">
                  {filteredCast.map((m) => (
                    <TobisPersonRow
                      key={m.personId}
                      name={m.personName}
                      photoUrl={m.personPhotoUrl}
                      title={m.personName ?? ""}
                      sub={
                        m.roleCount > 1
                          ? intl.formatMessage(
                              {
                                id: "tobis.title.castRoles",
                                defaultMessage: "{role} and {count} more",
                              },
                              { role: m.characterName ?? "", count: m.roleCount - 1 },
                            )
                          : // Reine Crew (Regie/Drehbuch, kein Schauspiel-Credit auf
                            // diesem Titel): die Jobs statt eines Charakternamens
                            // zeigen, sonst bliebe die Zeile leer.
                            (m.characterName ?? (m.jobs.length > 0 ? m.jobs.join(", ") : null))
                      }
                      to={`/tobis/people/${m.personId}`}
                      embedded
                    />
                  ))}
                </div>
              )}
            </Section>
          </div>
        </div>
      ) : null}

      {t && tvmazeId ? <TobisDossier kind="title" id={tvmazeId} /> : null}

      {admissions ? (
        <TobisAdmissionsModal
          open={admissionsOpen}
          onClose={() => setAdmissionsOpen(false)}
          titleName={t?.name ?? ""}
          admissions={admissions}
          // Nur die FFA-Zahl weitergeben: LUMIEREs eigene DE-Zeile steht schon
          // in der Marktliste, eine zweite Nennung waere doppelt.
          ffaAdmissionsDe={t?.admissionsSource === "ffa" ? t.admissionsDe : null}
        />
      ) : null}
    </TobisPageShell>
  );
}
